import ContactForm from "../atoms/ContactForm";
import ServicesHeader from "../atoms/ServicesHeader";
import Button from "../components/v2/Button";
import Input from "../components/v2/Input";
import Textarea from "../components/v2/TextArea";
import Typography from "../components/v2/Typography";
import useApp from "../hooks/useApp";

const DomainAppraisal = () => {

    const appCtx = useApp();

    return (
        <>
            <ServicesHeader active="/services/domain-appraisals" />
            {/* <!-- 1. HERO --> */}
            <section className="py-20 md:py-28 bg-white appraisal-service">
                <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <h1 className="text-5xl font-bold leading-tight mb-6">Accurate Domain Appraisals</h1>
                        <p className="text-xl mb-4">Instant snapshot or expert‑level valuation—choose what fits.</p>
                        <p className="text-lg mb-6 max-w-xl">Discover the real market value of your domain in minutes, or commission a detailed, human‑led appraisal backed by eight‑figure brokerage experience.</p>
                        <ul className="list-disc list-inside space-y-2 text-lg">
                            <li>Prevent under‑pricing and leaving six figures on the table</li>
                            <li>Avoid scare‑off over‑pricing that stalls negotiations</li>
                            <li>Strengthen investor or board due‑diligence with third‑party validation</li>
                        </ul>
                    </div>
                    {/* Callback Form */}
                    <div className="border-1 border-blue-500 p-8 shadow-sm bg-gray-50">
                        <h2 className="text-2xl font-semibold mb-4">Get Your Domain Appraised</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            console.log(e.target);
                            appCtx.submitForm(e.target.name.value, e.target.email.value, e.target.phone.value, e.target.query.value);
                        }} className="space-y-4">
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Name</Typography>
                                </div>
                                <Input isDark={true} name="name" label="" placeholder="John Doe" type="text" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Phone number</Typography>
                                </div>
                                <Input isDark={true} name="phone" label="" placeholder="+1 123456789" type="number" value="" onChange={() => { }} />
                            </div>
                            <div className="form-control">
                                <div className="form-name">
                                    <Typography variant="para">Email</Typography>
                                </div>
                                <Input isDark={true} name="email" label="" placeholder="johndoe@gmail.com" type="email" value="" onChange={() => { }} />
                            </div>
                            <Textarea isDark={true} name="query" label="" placeholder="Domain name you want appraised" value="" onChange={() => { }} style={{height: 170}} />
                            <Button variant="primary" type="submit" fullWidth>
                                <Typography variant="button">Submit</Typography>
                            </Button>
                        </form>
                        <p className="text-sm mt-2">Zero cost. Immediate estimate. No login required.</p>
                    </div>
                </div>
            </section>

            {/* 2. WHY IT MATTERS */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Why an Accurate Appraisal Matters</h2>
                    <ul className="grid md:grid-cols-2 gap-6 text-lg list-disc list-inside">
                        <li>Prevent under‑pricing and leaving six figures on the table.</li>
                        <li>Avoid scare‑off over‑pricing that stalls negotiations.</li>
                        <li>Strengthen investor or board due‑diligence with third‑party validation.</li>
                        <li>Benchmark ROI before launching paid acquisition campaigns.</li>
                    </ul>
                </div>
            </section>

            {/* 3. APPRAISAL OPTIONS */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Our Appraisal Options</h2>
                    <div className="grid md:grid-cols-2 gap-8 text-lg">
                        <div className="bg-slate-100 p-6 rounded-lg shadow-sm">
                            <h3 className="text-2xl font-semibold mb-2">Instant Snapshot <span className="text-sm">(Free)</span></h3>
                            <p className="mb-4">Automated estimate delivered in seconds via our online tool.</p>
                            <ul className="space-y-2 list-disc list-inside">
                                <li>Machine‑learning value range</li>
                                <li>Basic comparable sales list</li>
                                <li>Perfect for quick gut‑checks</li>
                            </ul>
                        </div>
                        <div className="bg-slate-100 p-6 rounded-lg shadow-sm">
                            <h3 className="text-2xl font-semibold mb-2">Professional Appraisal <span className="text-sm">$99</span></h3>
                            <p className="mb-4">Three‑page PDF report delivered within 48 hours.</p>
                            <ul className="space-y-2 list-disc list-inside">
                                <li>Human expert review & market comps</li>
                                <li>SEO & traffic metrics snapshot</li>
                                <li>Recommended pricing strategy</li>
                            </ul>
                        </div>
                        <div className="bg-slate-100 p-6 rounded-lg shadow-sm md:col-span-2">
                            <h3 className="text-2xl font-semibold mb-2">Premium Appraisal <span className="text-sm">$1,500</span></h3>
                            <p className="mb-4">10‑page deep‑dive report + 15‑min consultation. Delivered in 5 days.</p>
                            <ul className="space-y-2 list-disc list-inside">
                                <li>Full competitive landscape & buyer‑persona map</li>
                                <li>Liquidity score & sale‑probability analysis</li>
                                <li>Brokerage‑fee credit if you list with us within 60 days</li>
                            </ul>
                        </div>
                        <div className="bg-slate-100 p-6 rounded-lg shadow-sm md:col-span-2">
                            <h3 className="text-2xl font-semibold mb-2">Portfolio Valuation</h3>
                            <p className="mb-4">Custom quote for 20+ domains—ideal for investors & corporate portfolios.</p>
                            <p className="text-sm">Contact us for a tailored proposal.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. WHAT YOU GET */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">What's Inside a NameConnect Appraisal</h2>
                    <ul className="grid md:grid-cols-2 gap-4 text-lg list-disc list-inside">
                        <li>Verified comparable sales table (10+ recent comps)</li>
                        <li>SEO & traffic audit with SEMrush snapshot</li>
                        <li>Brandability & linguistic score</li>
                        <li>Buyer persona & industry demand analysis</li>
                        <li>Pricing guidance & sale‑timeline estimate</li>
                        <li>Action plan for market positioning</li>
                    </ul>
                </div>
            </section>

            {/* 5. PROCESS */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-semibold mb-8">Our 5‑Step Appraisal Process</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 text-lg order-2 lg:order-1">
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">1. Submit Request</h3>
                                <p>Simple form or instant tool.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">2. NDA & Data Collection</h3>
                                <p>We keep your info private.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">3. Expert Analysis</h3>
                                <p>Human review + proprietary datasets.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">4. Report Delivery</h3>
                                <p>PDF email with valuation range.</p>
                            </div>
                            <div>
                                <h3 className="text-2xl font-semibold mb-2">5. Optional Review Call</h3>
                                <p>Discuss next steps or brokerage potential (no pricing promises).</p>
                            </div>
                        </div>
                        <div className="flex justify-center lg:justify-end order-1 lg:order-2">
                            <img 
                                src="https://cdn.webstadia.com/b63c26c6-5829-4599-9d8d-46ce9dae7b8d.png" 
                                alt="5-Step Appraisal Process" 
                                className="w-full max-w-md lg:max-w-lg rounded-lg shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. FAQ */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 max-w-5xl">
                    <h2 className="text-3xl font-semibold mb-6">Frequently Asked Questions</h2>
                    <div className="space-y-6">
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">How accurate is the free tool?</summary>
                            <p className="pt-2">Our algorithm provides a useful range, but premium names need human insight for true market value.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Why pay for a professional appraisal?</summary>
                            <p className="pt-2">A human‑led report includes comps, buyer analysis, and pricing strategy—crucial for six‑figure or higher names.</p>
                        </details>
                        <details className="bg-white border border-gray-200 rounded-lg p-4">
                            <summary className="font-medium cursor-pointer">Does appraisal guarantee brokerage pricing?</summary>
                            <p className="pt-2">No. The appraisal offers a market range; final sale price depends on buyer demand and timing.</p>
                        </details>
                    </div>
                </div>
            </section>

            {/* 7. CTA */}
            <section className="py-20 bg-blue-600 text-white text-center">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-4xl font-bold mb-4">Ready to Discover Your Domain's True Value?</h2>
                    <p className="text-lg mb-8">Start with our free instant tool or commission a professional appraisal today.</p>
                    <div className="space-x-4">
                        <a href="/tool/appraisal" target="_blank" className="inline-block bg-white text-blue-600 font-semibold py-4 px-8 rounded">Instant Free Valuation</a>
                        <a href="/contact" className="inline-block bg-blue-800 text-white font-semibold py-4 px-8 rounded border-2 border-white">Order Professional Appraisal</a>
                    </div>
                </div>
            </section>
        </>
    );
};

export default DomainAppraisal;